import {
  type ClubImportSubsection,
  trackClubImportDone,
} from "@/events/clubImportMixpanel";
import { trackClubImportDoneConversion } from "@/events/googleAdsEvents";
import { clubClaimBlockedAlert } from "@/functions/clubClaimBlockedAlert";
import { sendMessageToExtension } from "@/functions/sendMessageToExtension";
import { syncExtensionStatusFromResponse } from "@/redux/clubImport/clubImportSlice";
import {
  markImportFinished,
  setIsCurrentlyImporting,
} from "@/redux/globalState/globalStateSlice";
import { addAlert, showModal } from "@/redux/message/messageSlice";
import { updateScope } from "@/redux/squadBuilder/defaultSquadBuilder/defaultSquadBuilderSlice";
import { AppDispatch } from "@/redux/store";
import {
  fetchClubImportState,
  fetchUserNotifications,
  getUserSelector,
} from "@/redux/user/userSlice";
import { useTranslation } from "@repo/shared/i18n";
import { fetchClubStats } from "@repo/shared/redux";
import { useState } from "react";
import { isSafari } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";

/**
 * What "Done" means, in one place: tell the extension the import is over, then
 * refresh everything that answer changes.
 *
 * All of it lives here rather than half here and half in the overlay that
 * renders it — "an import ended" is one event, and splitting its consequences
 * across a hook and a component means a future caller has to know both halves.
 * `markImportFinished` also clears the in-progress flag, so pages can notice a
 * finished import without a callback (see MyClub).
 *
 * `subsection` names the screen the import was started from, so a tutorial's
 * steps and its Done land in the same funnel.
 */
export const useFinishImport = (
  onFinished: () => void,
  subsection: ClubImportSubsection = "Overlay",
) => {
  const { t } = useTranslation(["clubImport", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector(getUserSelector);
  const [loading, setLoading] = useState(false);

  const finish = () => {
    // Logged out mid-import: nobody to import for. Close the overlay first so
    // the login modal is not stuck behind it.
    if (!user?.uuid) {
      dispatch(setIsCurrentlyImporting(false));
      onFinished();
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "import-club",
        }),
      );
      return;
    }
    setLoading(true);
    // Only a real import may stamp `lastImportFinishedAt` — pages run their
    // post-import work off it, so a refusal would look like a success.
    let imported = false;
    sendMessageToExtension({
      user,
      doneRequest: true,
      safariMobile: isSafari && !!localStorage.getItem("FROM_EXTENSION_APP"),
    })
      .then((res) => {
        if (res.clubClaimBlocked) {
          trackClubImportDone({ subsection, value: "blocked" });
          dispatch(addAlert(clubClaimBlockedAlert()));
          return;
        }
        trackClubImportDone({ subsection, value: "success" });
        trackClubImportDoneConversion();
        imported = true;
        dispatch(syncExtensionStatusFromResponse(res));
        dispatch(fetchUserNotifications());
        dispatch(updateScope("club"));
        /*
          The profile's playerCount is what the rest of the app reads to decide
          whether the club has players. Without this it stays at its pre-import
          value until a reload, so a freshly imported club still looks empty.
          The import also takes the club claim, so the profile's Unlink follows.
        */
        dispatch(fetchClubImportState());
        dispatch(fetchClubStats());
      })
      .catch(() => {
        trackClubImportDone({ subsection, value: "error" });
        dispatch(
          addAlert({
            type: "error",
            title: t("common:clubImport.alertTitle"),
            description: t("alerts.importFailed"),
          }),
        );
      })
      .finally(() => {
        setLoading(false);
        // The in-progress flag clears either way, or the overlay never closes.
        if (imported) dispatch(markImportFinished(Date.now()));
        else dispatch(setIsCurrentlyImporting(false));
        // A first import lands on My Club, whose welcome dialog greets it.
        onFinished();
      });
  };

  return { finish, loading };
};
